import { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';

interface DropdownItem {
  label: string;
  href: string;
  external?: boolean;
}

interface NavDropdownProps {
  label: string;
  items: DropdownItem[];
}

function NavDropdown({ label, items }: NavDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center space-x-1 text-sm text-gray-300 hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
      >
        <span>{label}</span>
        <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 w-44 bg-slate-800/95 backdrop-blur-md border border-slate-600/40 rounded-xl shadow-xl overflow-hidden z-50">
          {items.map((item) => (
            item.external ? (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-slate-700/60 transition-all duration-200 cursor-pointer"
              >
                <span>{item.label}</span>
                <i className="ri-external-link-line text-xs text-slate-500"></i>
              </a>
            ) : (
              <Link
                key={item.label}
                to={item.href}
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-slate-700/60 transition-all duration-200 cursor-pointer"
              >
                {item.label}
              </Link>
            )
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setMobileExpanded(null);
  }, [location]);

  const solutionItems: DropdownItem[] = [
    { label: 'Lumilake', href: 'https://lumilake.ai', external: true },
    { label: 'xp.io', href: 'https://xp.io', external: true },
  ];

  const useCaseItems: DropdownItem[] = [
    { label: 'lumid.market', href: 'https://lumid.market', external: true },
    { label: 'lumid.science', href: 'https://lumid.science', external: true },
  ];

  const toggleMobile = (label: string) => {
    setMobileExpanded(mobileExpanded === label ? null : label);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lumid' : 'bg-slate-900/80 backdrop-blur-sm'
    }`}>
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group cursor-pointer">
            <span className="font-artistic text-lg font-medium text-white group-hover:lumid-text-shine transition-all duration-300 tracking-wider">
              LUMID
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-6">
            <a href="#about" className="text-sm text-gray-300 hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap">
              About Lumid
            </a>
            <NavDropdown label="Solutions" items={solutionItems} />
            <NavDropdown label="Use Cases" items={useCaseItems} />
            <Link to="/labs" className="text-sm text-gray-300 hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap">
              AI Labs
            </Link>
            <a
              href="/auth/login"
              className="text-sm px-4 py-1.5 border border-slate-500/60 text-gray-300 hover:text-white hover:border-slate-400 rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Log In
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-gray-300 hover:text-white transition-colors duration-300 cursor-pointer p-2"
            aria-label="Toggle menu"
          >
            <i className={`text-lg transition-transform duration-300 ${isMenuOpen ? 'ri-close-line rotate-180' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        <div className={`md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen ? 'max-h-96 opacity-100 visible' : 'max-h-0 opacity-0 invisible overflow-hidden'
        }`}>
          <div className="py-4 space-y-1 border-t border-slate-700/50">
            {/* About Lumid */}
            <a
              href="#about"
              className="flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-300 cursor-pointer"
              onClick={() => setIsMenuOpen(false)}
            >
              <i className="ri-information-line mr-2"></i>
              About Lumid
            </a>

            {/* Solutions */}
            <div>
              <button
                onClick={() => toggleMobile('solutions')}
                className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-300 cursor-pointer"
              >
                <span>Solutions</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${mobileExpanded === 'solutions' ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileExpanded === 'solutions' && (
                <div className="ml-4 mt-1 space-y-1">
                  {solutionItems.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center justify-between px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-300 cursor-pointer"
                    >
                      <span>{item.label}</span>
                      <i className="ri-external-link-line text-xs text-slate-500"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Use Cases */}
            <div>
              <button
                onClick={() => toggleMobile('usecases')}
                className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-300 cursor-pointer"
              >
                <span>Use Cases</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${mobileExpanded === 'usecases' ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileExpanded === 'usecases' && (
                <div className="ml-4 mt-1 space-y-1">
                  {useCaseItems.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center justify-between px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-300 cursor-pointer"
                    >
                      <span>{item.label}</span>
                      <i className="ri-external-link-line text-xs text-slate-500"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* AI Labs */}
            <Link
              to="/labs"
              className="flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-300 cursor-pointer"
              onClick={() => setIsMenuOpen(false)}
            >
              <i className="ri-flask-line mr-2"></i>
              AI Labs
            </Link>

            {/* Log In */}
            <a
              href="/auth/login"
              className="flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-300 cursor-pointer"
              onClick={() => setIsMenuOpen(false)}
            >
              <i className="ri-login-box-line mr-2"></i>
              Log In
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}